import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import moment, { Moment } from 'moment';
import { Button, Table, DatePicker } from 'antd';
import pluralize from 'pluralize';
import cx from 'classnames';
import {
  ColumnsType,
  FilterValue,
  SorterResult,
  TablePaginationConfig,
} from 'antd/lib/table/interface';
import { RangeValue } from 'rc-picker/lib/interface';
import { useDidUpdate } from 'rooks';

import { SortingDirectionEnum } from 'enums';
import { pageDefaultSizeTable } from 'constants/index';
import { DateParams } from 'utils/filterUtils';
import useSortColumns, { ISortParams } from 'hooks/useSortColumns';
import {
  getAverageRatesReportData,
  getAverageRatesReportExcel,
} from 'redux/reducers/homeDashboardSlice';
import { getSortOrderRequestValue } from 'utils/sortUtils';
import {
  disableDatesExceptSundays,
  getDateRangeTillLastSunday,
  getDateWithoutTime,
} from 'utils/dateUtils';
import LastSyncDateSection from 'components/LastSyncDateSection';
import { downloadFileByObjectURL } from 'utils/fileUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import {
  AverageRatesSortFieldEnum,
  AverageRatesByCustomerReportItemViewModel,
  AverageRatesByCustomerReportViewModel,
} from 'api/backofficeApiClient';
import BaseTableHeader from 'components/wrappers/BaseTableHeader';
import { useAppDispatch } from 'hooks/useAppDispatch';
import BasePageLabel from 'components/wrappers/BasePageLabel';

import styles from './AverageRatesCustomerReportDetailsPage.module.less';
import commonStyles from '../UserHomePage.module.less';

enum AverageRatesFilterTypes {
  Date = 'date',
}

type AverageRatesReportFilters = {
  [AverageRatesFilterTypes.Date]: DateParams | undefined;
};

const defaultSortParams: ISortParams<AverageRatesSortFieldEnum> = {
  sortOrder: 'descend',
  sortBy: AverageRatesSortFieldEnum.Markup,
};

const dateRangeDurationInWeeks = 7;

const columns: ColumnsType<AverageRatesByCustomerReportItemViewModel> = [
  {
    title: 'Customer',
    key: AverageRatesSortFieldEnum.Customer,
    render: (record: AverageRatesByCustomerReportItemViewModel) => (
      <Link to={`/customer/${record.customerKey}`}>{record.customerName}</Link>
    ),
  },
  {
    title: 'Avg Bill Rate',
    key: AverageRatesSortFieldEnum.AvgBillRate,
    render: (record: AverageRatesByCustomerReportItemViewModel) =>
      record.avgBillRate,
  },
  {
    title: 'Avg Pay Rate',
    key: AverageRatesSortFieldEnum.AvgPayRate,
    render: (record: AverageRatesByCustomerReportItemViewModel) =>
      record.avgPayRate,
  },
  {
    title: 'Spread $',
    key: AverageRatesSortFieldEnum.Spread,
    render: (record: AverageRatesByCustomerReportItemViewModel) =>
      record.spread,
  },
  {
    title: 'Markup %',
    key: AverageRatesSortFieldEnum.Markup,
    render: (record: AverageRatesByCustomerReportItemViewModel) =>
      record.markup,
  },
];

export default function AverageRatesCustomerReportDetailsPage() {
  const [isAverageRatesReportDataLoading, setIsAverageRatesReportDataLoading] =
    useState(false);
  const [averageRatesReportData, setAverageRatesReportData] =
    useState<AverageRatesByCustomerReportViewModel>();
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [currentTablePage, setCurrentTablePage] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    sortBy,
    sortOrder,
    averageStartDate: startDate,
    averageEndDate: endDate,
    section,
  } = queryString.parse(location.search);
  const initialSortParams: ISortParams<AverageRatesSortFieldEnum> =
    sortBy && sortOrder
      ? {
          sortBy: parseInt(sortBy),
          sortOrder,
        }
      : defaultSortParams;
  const defaultDateFilter: DateParams = getDateRangeTillLastSunday(
    dateRangeDurationInWeeks,
  );
  const initialFilters = {
    [AverageRatesFilterTypes.Date]:
      startDate && endDate
        ? {
            startDate: moment(startDate, 'MM/DD/YYYY'),
            endDate: moment(endDate, 'MM/DD/YYYY'),
          }
        : defaultDateFilter,
  };
  const [filters, setFilters] =
    useState<AverageRatesReportFilters>(initialFilters);
  const dispatch = useAppDispatch();

  const averageRatesReportCountMessage = pluralize(
    'customer',
    averageRatesReportData?.totalCount || 0,
    true,
  );

  const { sortColumns, sortParams, setSortParams } = useSortColumns(
    columns,
    initialSortParams,
  );

  useDidUpdate(() => {
    const queryData = {
      section,
      sortBy: sortParams.sortBy,
      sortOrder: sortParams.sortOrder,
      averageStartDate: filters[AverageRatesFilterTypes.Date]?.startDate
        ? filters[AverageRatesFilterTypes.Date]?.startDate?.format('MM/DD/YYYY')
        : undefined,
      averageEndDate: filters[AverageRatesFilterTypes.Date]?.endDate
        ? filters[AverageRatesFilterTypes.Date]?.endDate?.format('MM/DD/YYYY')
        : undefined,
    };

    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
    // eslint-disable-next-line
  }, [sortParams.sortBy, sortParams.sortOrder, filters]);

  const loadAverageRatesReportData = useCallback(async () => {
    setIsAverageRatesReportDataLoading(true);
    const result = await dispatch(
      getAverageRatesReportData({
        pageNumber: currentTablePage - 1,
        sortField: sortParams.sortBy,
        sortType: getSortOrderRequestValue(sortParams.sortOrder),
        startDate: filters[AverageRatesFilterTypes.Date]?.startDate
          ? getDateWithoutTime(filters[AverageRatesFilterTypes.Date]?.startDate)
          : undefined,
        endDate: filters[AverageRatesFilterTypes.Date]?.endDate
          ? getDateWithoutTime(filters[AverageRatesFilterTypes.Date]?.endDate)
          : undefined,
        pageSize: pageDefaultSizeTable,
      }),
    );

    if (getAverageRatesReportData.fulfilled.match(result)) {
      setAverageRatesReportData(result.payload);
    } else {
      setAverageRatesReportData({
        items: [],
        totalCount: 0,
        grandTotal: undefined,
      });
    }

    setIsAverageRatesReportDataLoading(false);
  }, [
    dispatch,
    currentTablePage,
    sortParams.sortOrder,
    sortParams.sortBy,
    filters,
  ]);

  const loadInitialData = useCallback(async () => {
    await loadAverageRatesReportData();

    setIsInitialDataLoaded(true);
  }, [loadAverageRatesReportData]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filter: Record<string, FilterValue | null>,
    sort:
      | SorterResult<AverageRatesByCustomerReportItemViewModel>
      | SorterResult<AverageRatesByCustomerReportItemViewModel>[],
  ) => {
    const sortOrder =
      (sort as SorterResult<AverageRatesByCustomerReportItemViewModel>).order ||
      SortingDirectionEnum.Ascend;
    const sortBy = (
      sort as SorterResult<AverageRatesByCustomerReportItemViewModel>
    ).columnKey as AverageRatesSortFieldEnum;

    setSortParams(({ sortBy: prevSortBy, sortOrder: prevSortOrder }) => {
      if (prevSortBy !== sortBy || prevSortOrder !== sortOrder) {
        // Reset table page if sorting has changed
        setCurrentTablePage(1);
      } else {
        setCurrentTablePage(pagination.current || 1);
      }

      return {
        sortOrder,
        sortBy,
      };
    });
  };

  const handleSelectDate =
    (type: AverageRatesFilterTypes) => (date: RangeValue<Moment>) => {
      if (date) {
        setFilters({
          ...filters,
          [type]: {
            startDate: date[0],
            endDate: date[1],
          },
        });
      } else {
        setFilters({
          ...filters,
          [type]: undefined,
        });
      }
      setCurrentTablePage(1);
    };

  const handleDownloadReport = async () => {
    const result = await dispatch(
      getAverageRatesReportExcel({
        pageNumber: currentTablePage - 1,
        sortField: sortParams.sortBy,
        sortType: getSortOrderRequestValue(sortParams.sortOrder),
        startDate: filters[AverageRatesFilterTypes.Date]?.startDate
          ? getDateWithoutTime(filters[AverageRatesFilterTypes.Date]?.startDate)
          : undefined,
        endDate: filters[AverageRatesFilterTypes.Date]?.endDate
          ? getDateWithoutTime(filters[AverageRatesFilterTypes.Date]?.endDate)
          : undefined,
        pageSize: pageDefaultSizeTable,
      }),
    );

    if (getAverageRatesReportExcel.fulfilled.match(result)) {
      downloadFileByObjectURL(result.payload.url, result.payload.fileName);
    } else {
      BaseAlert.error(
        result.error.message ? result.error.message : ApiErrorMessage,
      );
    }
  };

  return (
    <div className="page-wrapper scroll-box">
      <div className="page-holder">
        <BasePageLabel label="Dashboard details">
          {averageRatesReportData?.lastSyncDate && (
            <div className="title-line__info">
              <LastSyncDateSection
                syncDate={averageRatesReportData.lastSyncDate}
                title="Average rates report is automatically refreshed every 15 minutes."
              />
            </div>
          )}
        </BasePageLabel>
        <div className="page-box page-inner">
          <div className="page-inner-container">
            <BaseTableHeader
              enableSubTitle={isInitialDataLoaded}
              subTitle={averageRatesReportCountMessage}
              title="Average Bill and Pay Rates by Customer - Trailing 8 Weeks"
            >
              <div>
                <DatePicker.RangePicker
                  format="MM/DD/YYYY"
                  allowClear={false}
                  separator="-"
                  disabledDate={disableDatesExceptSundays}
                  value={
                    filters[AverageRatesFilterTypes.Date]
                      ? [
                          moment(
                            filters[AverageRatesFilterTypes.Date]?.startDate,
                          ),
                          moment(
                            filters[AverageRatesFilterTypes.Date]?.endDate,
                          ),
                        ]
                      : undefined
                  }
                  onChange={handleSelectDate(AverageRatesFilterTypes.Date)}
                />
              </div>
              <div>
                <Button
                  className={commonStyles.saveBtn}
                  onClick={handleDownloadReport}
                  icon={<span className="icon icon-xls-file" />}
                />
              </div>
            </BaseTableHeader>
            <Table
              className={cx(styles.table, 'custom-table')}
              pagination={{
                total: averageRatesReportData?.totalCount,
                pageSize: pageDefaultSizeTable,
                current: currentTablePage,
                showSizeChanger: false,
              }}
              summary={() => (
                <>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={1}>
                      All Customers
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2}>
                      {averageRatesReportData?.grandTotal?.avgBillRateTotal}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>
                      {averageRatesReportData?.grandTotal?.avgPayRateTotal}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>
                      {averageRatesReportData?.grandTotal?.spreadTotal}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>
                      {averageRatesReportData?.grandTotal?.markupTotal}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </>
              )}
              showSorterTooltip={false}
              onChange={handleTableChange}
              columns={sortColumns}
              dataSource={averageRatesReportData?.items}
              rowKey={(record) => record?.customerKey as string}
              loading={isAverageRatesReportDataLoading}
              scroll={{ x: '100%' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
